import { v4 as uuidv4 } from 'uuid';
import { ICartService } from '@/domain/services/cart.service.interface';
import { CartEntity, CartItemEntity as StoredCartItem, CartTotals } from '@/domain/entities/cart.entity';
import { CartItemEntity } from '@/domain/entities/cart-item.entity';
import { CurrencyAmount } from '@/domain/value-objects/currency-amount.vo';
import type { Cart, CartItem } from '@template-project/storefront-bff-client';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import { applyPackAnnotations, forgetPack, rememberPackLines } from '@/lib/cart/pack-cart-annotations';
import { Logger } from '@template-project/storefront-shared';

function dollarStringToCents(dollars: string): CurrencyAmount {
  const cents = Math.round(Number.parseFloat(dollars) * 100);
  return { base: cents, display: cents };
}

function mapBffCartItemToInput(item: StoredCartItem): {
  product_uuid: string;
  variant_uuid: string;
  quantity: number;
  pack_id?: string;
  pack_item_id?: string;
} {
  return {
    product_uuid: item.product_id,
    variant_uuid: item.variant_id,
    quantity: item.quantity,
    ...(item.pack_id ? { pack_id: item.pack_id } : {}),
    ...(item.pack_item_id ? { pack_item_id: item.pack_item_id } : {}),
  };
}

function mapBffCartToEntity(cart: Cart): CartEntity {
  const items: StoredCartItem[] = cart.items.map((item: CartItem, index: number) => ({
    id: item.pack_id
      ? `${item.pack_id}-${item.pack_item_id ?? item.variant_id}-${index}`
      : `${item.product_id}-${item.variant_id}-${index}`,
    product_id: item.product_id,
    variant_id: item.variant_id,
    quantity: item.quantity,
    name: item.name,
    price: dollarStringToCents(item.unit_price.store),
    image_url: item.image_url,
    compare_at_price: item.compare_at_price,
    pack_id: item.pack_id,
    pack_item_id: item.pack_item_id,
    variation_attributes: item.variation_attributes,
  }));

  const mapTotals = (t: Cart['totals']): CartTotals => ({
    items: dollarStringToCents(t.items),
    discounts: dollarStringToCents(t.discounts),
    taxes: dollarStringToCents(t.taxes),
    shipping: dollarStringToCents(t.shipping),
    fees: dollarStringToCents(t.fees),
    total: dollarStringToCents(t.total),
  });

  return {
    token: cart.token,
    items,
    totals: mapTotals(cart.totals),
    currency: cart.currency.main,
    country_code: cart.country_code,
    vendor: cart.vendor,
    shop: cart.store,
    status: cart.status,
    upsell_funnel_id: cart.upsell_funnel_id,
    upsell_first_step_id: cart.upsell_first_step_id,
  };
}

function cartCurrencyRatio(options?: { currencyRatio?: number }): number {
  const ratio = options?.currencyRatio;
  if (ratio === undefined || !Number.isFinite(ratio) || ratio <= 0) {
    return 1;
  }
  return ratio;
}

export class CartBffService implements ICartService {
  private readonly client = createBffClient();

  async getCart(token: string): Promise<CartEntity | null> {
    try {
      const res = await this.client.getCart(token);
      return applyPackAnnotations(mapBffCartToEntity(res.data), token);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return null;
      const message = err instanceof Error ? err.message : String(err);
      Logger.error(`[CartBffService] getCart error: ${message}`);
      return null;
    }
  }

  async createCart(options?: {
    countryCode?: string;
    currency?: string;
    currencyRatio?: number;
    vendor?: string;
    shop?: string;
    upsellFunnelId?: string;
  }): Promise<CartEntity> {
    const cartToken = uuidv4();
    try {
      const res = await this.client.createCart({
        token: cartToken,
        vendor: options?.vendor ?? 'shapermint',
        store: options?.shop ?? 'shapermint',
        country_code: options?.countryCode ?? 'US',
        currency_code: options?.currency ?? 'USD',
        local_currency_code: options?.currency ?? 'USD',
        currency_ratio: cartCurrencyRatio(options),
        items: [],
        ...(options?.upsellFunnelId ? { upsell_funnel_id: options.upsellFunnelId } : {}),
      });
      return mapBffCartToEntity(res.data);
    } catch (err) {
      Logger.error(`[CartBffService] createCart error: ${String(err)}`);
      throw err;
    }
  }

  async createCartWithItem(
    item: CartItemEntity,
    options?: {
      countryCode?: string;
      currency?: string;
      currencyRatio?: number;
      vendor?: string;
      shop?: string;
      upsellFunnelId?: string;
    }
  ): Promise<{ success: boolean; cartToken: string }> {
    const cartToken = uuidv4();
    try {
      await this.client.createCart({
        token: cartToken,
        vendor: options?.vendor ?? 'shapermint',
        store: options?.shop ?? 'shapermint',
        country_code: options?.countryCode ?? 'US',
        currency_code: options?.currency ?? 'USD',
        local_currency_code: options?.currency ?? 'USD',
        currency_ratio: cartCurrencyRatio(options),
        items: [
          {
            product_uuid: item.product_id,
            variant_uuid: item.variant_id,
            quantity: item.quantity,
          },
        ],
        ...(options?.upsellFunnelId ? { upsell_funnel_id: options.upsellFunnelId } : {}),
      });
      return { success: true, cartToken };
    } catch (err) {
      Logger.error(`[CartBffService] createCartWithItem error: ${String(err)}`);
      throw err;
    }
  }

  async addToCart(
    cartToken: string,
    item: CartItemEntity,
    options?: { currencyRatio?: number; upsellFunnelId?: string },
  ): Promise<{ success: boolean; cartToken: string }> {
    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const existingItems = currentCart.items.map(mapBffCartItemToInput);

    const existingIndex = existingItems.findIndex(
      (i) =>
        i.product_uuid === item.product_id &&
        i.variant_uuid === item.variant_id &&
        !i.pack_id
    );

    if (existingIndex === -1) {
      existingItems.push({
        product_uuid: item.product_id,
        variant_uuid: item.variant_id,
        quantity: item.quantity,
      });
    } else {
      existingItems[existingIndex].quantity += item.quantity;
    }

    try {
      await this.client.updateCart(cartToken, {
        token: cartToken,
        vendor: currentCart.vendor ?? 'shapermint',
        store: currentCart.shop ?? 'shapermint',
        country_code: currentCart.country_code ?? 'US',
        currency_code: currentCart.currency ?? 'USD',
        local_currency_code: currentCart.currency ?? 'USD',
        currency_ratio: cartCurrencyRatio(options),
        items: existingItems,
        ...(options?.upsellFunnelId || currentCart.upsell_funnel_id
          ? { upsell_funnel_id: options?.upsellFunnelId ?? currentCart.upsell_funnel_id }
          : {}),
        ...(currentCart.upsell_first_step_id
          ? { upsell_first_step_id: currentCart.upsell_first_step_id }
          : {}),
      });
      return { success: true, cartToken };
    } catch (err) {
      Logger.error(`[CartBffService] addToCart error: ${String(err)}`);
      throw err;
    }
  }

  async removeCartItem(
    cartToken: string,
    productId: string,
    variantId: string
  ): Promise<CartEntity> {
    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items
      .filter((i) => i.product_id !== productId || i.variant_id !== variantId)
      .map(mapBffCartItemToInput);

    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async updateCartItem(
    cartToken: string,
    productId: string,
    variantId: string,
    quantity: number
  ): Promise<CartEntity> {
    if (quantity <= 0) {
      return this.removeCartItem(cartToken, productId, variantId);
    }

    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items.map((i) => ({
      ...mapBffCartItemToInput(i),
      quantity: i.product_id === productId && i.variant_id === variantId ? quantity : i.quantity,
    }));

    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async removePackFromCart(cartToken: string, packId: string): Promise<CartEntity> {
    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items
      .filter((i) => i.pack_id !== packId)
      .map(mapBffCartItemToInput);

    forgetPack(cartToken, packId);
    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async updatePackQuantity(
    cartToken: string,
    packId: string,
    quantity: number
  ): Promise<CartEntity> {
    if (quantity <= 0) {
      return this.removePackFromCart(cartToken, packId);
    }

    const currentCart = await this.getCart(cartToken);
    if (!currentCart) {
      throw new Error('Cart not found');
    }

    const updatedItems = currentCart.items.map((i) => ({
      ...mapBffCartItemToInput(i),
      quantity: i.pack_id === packId ? quantity : i.quantity,
    }));

    return this.putCart(cartToken, currentCart, updatedItems);
  }

  async addPackToCart(
    cartToken: string,
    packId: string,
    selections?: Array<{ packItemId?: string; productId: string; variantId: string }>,
  ): Promise<{ success: boolean; cartToken: string }> {
    try {
      await this.client.addPackToCart(cartToken, {
        pack_id: packId,
        selections: selections?.map((s) => ({
          pack_item_id: s.packItemId,
          product_id: s.productId,
          variant_id: s.variantId,
        })),
      });
      if (selections?.length) {
        rememberPackLines(cartToken, packId, selections);
      }
      return { success: true, cartToken };
    } catch (error) {
      Logger.error(`[CartBffService] addPackToCart error: ${error}`);
      throw error;
    }
  }

  private async putCart(
    cartToken: string,
    currentCart: CartEntity,
    items: Array<{
      product_uuid: string;
      variant_uuid: string;
      quantity: number;
      pack_id?: string;
      pack_item_id?: string;
    }>
  ): Promise<CartEntity> {
    try {
      const res = await this.client.updateCart(cartToken, {
        token: cartToken,
        vendor: currentCart.vendor ?? 'shapermint',
        store: currentCart.shop ?? 'shapermint',
        country_code: currentCart.country_code ?? 'US',
        currency_code: currentCart.currency ?? 'USD',
        local_currency_code: currentCart.currency ?? 'USD',
        currency_ratio: 1,
        items,
      });
      return applyPackAnnotations(mapBffCartToEntity(res.data), cartToken);
    } catch (err) {
      Logger.error(`[CartBffService] putCart error: ${String(err)}`);
      throw err;
    }
  }
}
